import React, { useState, useEffect } from 'react';
import PoeAuthWindow from '../Auth/PoeAuthWindow';
import StashExplorer from '../Stash/StashExplorer';
import LeagueSelector from '../LeagueSelector/LeagueSelector';

interface StashExplorerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const StashExplorerModal: React.FC<StashExplorerModalProps> = ({ isOpen, onClose }) => {
  const [accessToken, setAccessToken] = useState<string | null>(localStorage.getItem('poe_access_token'));
  const [error, setError] = useState<string | null>(null);
  const [league, setLeague] = useState<string>('Phrecia');
  const [username, setUsername] = useState<string | null>(localStorage.getItem('poe_username'));
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(
    localStorage.getItem('poe_token_expires_at') 
      ? parseInt(localStorage.getItem('poe_token_expires_at') || '0') 
      : null
  );

  // Handle successful authentication
  const handleAuthSuccess = (token: string, user: string | undefined, expiresAt: number) => {
    setAccessToken(token);
    localStorage.setItem('poe_access_token', token);
    
    if (user) {
      setUsername(user);
      localStorage.setItem('poe_username', user);
    }
    
    setTokenExpiresAt(expiresAt);
    localStorage.setItem('poe_token_expires_at', expiresAt.toString());
    
    setError(null);
  };

  // Handle error from any component
  const handleError = (message: string) => {
    setError(message);
  };

  // Handle league change
  const handleLeagueChange = (newLeague: string) => {
    setLeague(newLeague);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('poe_access_token');
    localStorage.removeItem('poe_username');
    localStorage.removeItem('poe_token_expires_at');
    setAccessToken(null);
    setUsername(null);
    setTokenExpiresAt(null);
    setError(null);
  };

  // Check for token expiration
  useEffect(() => {
    if (accessToken && tokenExpiresAt && Date.now() > tokenExpiresAt) {
      // Token has expired
      handleLogout();
      setError('Your session has expired. Please log in again.');
    }
  }, [accessToken, tokenExpiresAt]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-75 overflow-y-auto">
      <div className="relative bg-gray-900 rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center border-b border-gray-700 px-6 py-4">
          <h2 className="text-xl font-bold text-white">In-Game Stash Explorer <sup className="ml-1 text-xs bg-red-500 text-white px-1 rounded-sm">BETA</sup></h2> 
          <button 
            className="text-gray-500 hover:text-white transition-colors"
            onClick={onClose}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div className="p-6">
          {error && (
            <div className="text-red-500 p-4 mb-4 bg-red-100/10 rounded border border-red-500">
              <p className="font-medium">Error:</p>
              <p>{error}</p>
            </div>
          )}

          <PoeAuthWindow 
            onAuthSuccess={handleAuthSuccess}
            onError={handleError}
            isAuthenticated={!!accessToken}
            username={username}
            tokenExpiresAt={tokenExpiresAt}
            onLogout={handleLogout}
          />

          {accessToken && (
            <div>
              <div className="flex justify-end mb-4">
                <LeagueSelector 
                  value={league}
                  onChange={handleLeagueChange}
                />
              </div>
              
              <StashExplorer 
                accessToken={accessToken}
                league={league}
                onError={handleError}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StashExplorerModal;